import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { ErrorBox, Field, Loading, Success, fmtDate } from '../components/ui';

interface Run {
  id: string;
  source: string;
  status: 'running' | 'completed' | 'stopped' | 'blocked' | 'failed' | 'interrupted';
  startedAt: string;
  finishedAt: string | null;
  pagesListed: number;
  pagesFetched: number;
  pagesUnchanged: number;
  pagesFailed: number;
  productsMatched: number;
  imagesSaved: number;
  lastError: string | null;
  startedBy?: string;
}
interface Source {
  code: string;
  name: string;
  baseUrl: string;
  currency: string;
  enabled: boolean;
  requestIntervalMs: number;
  notes: string | null;
  pagesRead: number;
  productsMatched: number;
  images: number;
  lastRun: Run | null;
}
interface Overview {
  sources: Source[];
  coverage: { products: number; withWebData: number };
  settings: { nightly: boolean; hour: number; autoAddProducts: boolean; photosForWebProducts: boolean };
  webProducts: {
    addable: number;
    ambiguous: { sku: string; names: string[] }[];
    sample: { sku: string; name: string; sources: string[] }[];
    masterListImported: boolean;
  };
}

const STATUS: Record<Run['status'], string> = {
  running: 'Running…',
  completed: 'Completed',
  stopped: 'Stopped',
  blocked: 'Stopped: site refused requests',
  failed: 'Failed',
  interrupted: 'Interrupted (server restarted)',
};

const progress = (r: Run) => {
  const done = r.pagesFetched + r.pagesUnchanged + r.pagesFailed;
  return r.pagesListed ? `${done.toLocaleString()} of ${r.pagesListed.toLocaleString()} pages` : 'reading site map…';
};

export function WebDataPage() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['webSources'],
    queryFn: () => api<Overview>('/web-sources'),
    refetchInterval: (query) => (query.state.data?.sources.some((s) => s.lastRun?.status === 'running') ? 3000 : false),
  });
  const runs = useQuery({ queryKey: ['webRuns'], queryFn: () => api<{ runs: Run[] }>('/web-sync/runs').then((r) => r.runs) });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const running = q.data?.sources.some((s) => s.lastRun?.status === 'running');
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['webSources'] });
    qc.invalidateQueries({ queryKey: ['webRuns'] });
  };
  useEffect(() => {
    if (!running) qc.invalidateQueries({ queryKey: ['webRuns'] });
  }, [running, qc]);

  const act = async (fn: () => Promise<unknown>, ok?: string) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setMsg(null);
    try {
      await fn();
      if (ok) setMsg(ok);
      refresh();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  const d = q.data!;

  return (
    <div>
      <h1>Costco product data</h1>
      <p className="muted">
        Collects long titles, descriptions, specifications and photos from Costco's websites (at Costco Canada's request) and matches them to your products by
        <strong> exact item number</strong>. Nothing is changed on a product automatically: staff choose what to use. Requests are slow and carry no identifying information (no business name, email or website), robots.txt is followed,
        and a run stops if a site refuses.
      </p>
      <ErrorBox error={error} />
      {msg && <Success>{msg}</Success>}

      <div className="stats">
        <div className="stat ok">
          <b>{d.coverage.withWebData.toLocaleString()}</b> of {d.coverage.products.toLocaleString()} products have web data
        </div>
      </div>

      <SettingsCard s={d.settings} onSaved={refresh} />

      <section className="card">
        <h2>Items found online that are not in your products</h2>
        <p className="small muted">
          Added only from costco.ca and the Business Centre, only when the item number is not already a product, and only when every page with that number gives the same title. They
          are marked "Costco website" and link to their page. Items whose pages disagree are listed for you to check.
        </p>
        {!d.webProducts.masterListImported && (
          <div className="alert alert-warn">
            Import the master item list first (Import products). Until then nothing is added, so items that are in the master list keep their master-list values.
          </div>
        )}
        <div className="stats">
          <div className="stat ok">
            <b>{d.webProducts.addable.toLocaleString()}</b> ready to add
          </div>
          <div className={`stat ${d.webProducts.ambiguous.length ? 'bad' : ''}`}>
            <b>{d.webProducts.ambiguous.length.toLocaleString()}</b> to check (pages disagree)
          </div>
        </div>
        <div className="row gap wrap">
          <button
            className="btn btn-primary"
            disabled={busy || running || d.webProducts.addable === 0 || !d.webProducts.masterListImported}
            onClick={() =>
              confirm(`Add ${d.webProducts.addable.toLocaleString()} items from Costco web pages as products?`) &&
              act(async () => {
                const r = await api<{ added: number }>('/web-products/add', { method: 'POST' });
                setMsg(`Added ${r.added.toLocaleString()} products.`);
              })
            }
          >
            Add {d.webProducts.addable.toLocaleString()} as products
          </button>
          {d.webProducts.ambiguous.length > 0 && (
            <a className="btn" href="/api/web-products/ambiguous.csv">
              Download list to check
            </a>
          )}
        </div>
        {d.webProducts.sample.length > 0 && (
          <details>
            <summary>Examples</summary>
            <ul className="small">
              {d.webProducts.sample.slice(0, 20).map((s) => (
                <li key={s.sku}>
                  <span className="mono">{s.sku}</span> {s.name}
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>

      <section className="card">
        <div className="row between wrap">
          <h2>Sites</h2>
          <div className="row gap">
            <button className="btn btn-primary" disabled={busy || running} onClick={() => act(() => api('/web-sync/start', { body: { source: 'all' } }), 'Started. Progress updates below.')}>
              Update all enabled sites
            </button>
            {running && (
              <button className="btn btn-danger" disabled={busy} onClick={() => act(() => api('/web-sync/stop', { method: 'POST' }), 'Stopping after the current page…')}>
                Stop
              </button>
            )}
          </div>
        </div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Site</th>
                <th>Enabled</th>
                <th>Pause between requests</th>
                <th>Pages read</th>
                <th>Your products matched</th>
                <th>Photos</th>
                <th>Last run</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {d.sources.map((s) => (
                <tr key={s.code}>
                  <td>
                    <a href={s.baseUrl} target="_blank" rel="noreferrer">
                      {s.name}
                    </a>
                    {s.notes && <div className="small muted">{s.notes}</div>}
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={s.enabled}
                      disabled={busy}
                      onChange={(e) => act(() => api(`/web-sources/${s.code}`, { method: 'PATCH', body: { enabled: e.target.checked } }))}
                      aria-label={`Enable ${s.name}`}
                    />
                  </td>
                  <td>
                    <select
                      value={s.requestIntervalMs}
                      disabled={busy}
                      onChange={(e) => act(() => api(`/web-sources/${s.code}`, { method: 'PATCH', body: { requestIntervalMs: Number(e.target.value) } }))}
                      aria-label="Pause between requests"
                    >
                      {[2000, 3000, 5000, 10000].map((ms) => (
                        <option key={ms} value={ms}>
                          {ms / 1000} s
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>{s.pagesRead.toLocaleString()}</td>
                  <td>{s.productsMatched.toLocaleString()}</td>
                  <td>{s.images.toLocaleString()}</td>
                  <td className="small">
                    {s.lastRun ? (
                      <>
                        <strong className={s.lastRun.status === 'blocked' || s.lastRun.status === 'failed' ? 'bad-text' : ''}>{STATUS[s.lastRun.status]}</strong>
                        <div>{s.lastRun.status === 'running' ? progress(s.lastRun) : fmtDate(s.lastRun.finishedAt ?? s.lastRun.startedAt)}</div>
                        {s.lastRun.lastError && s.lastRun.status !== 'completed' && <div className="muted">{s.lastRun.lastError}</div>}
                      </>
                    ) : (
                      <span className="muted">never</span>
                    )}
                  </td>
                  <td>
                    <button
                      className="btn btn-small"
                      disabled={busy || running || !s.enabled}
                      onClick={() => act(() => api('/web-sync/start', { body: { source: s.code } }), `Started ${s.name}.`)}
                    >
                      Update
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="small muted">
          The first full read of a site takes many hours (about 16,000 pages on costco.ca at one page every 3 seconds is roughly 13–14 hours). Later runs only read pages Costco has
          changed. sameday.costco.com is not included: its robots.txt forbids automated access.
        </p>
      </section>

      <section className="card">
        <h2>Recent runs</h2>
        {runs.data?.length === 0 && <p className="muted">No runs yet.</p>}
        {!!runs.data?.length && (
          <div className="table-wrap">
            <table className="table small">
              <thead>
                <tr>
                  <th>Started</th>
                  <th>Site</th>
                  <th>By</th>
                  <th>Status</th>
                  <th>Listed</th>
                  <th>Read</th>
                  <th>Unchanged</th>
                  <th>Failed</th>
                  <th>Matched</th>
                  <th>Photos</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {runs.data.map((r) => (
                  <tr key={r.id}>
                    <td>{fmtDate(r.startedAt)}</td>
                    <td>{d.sources.find((s) => s.code === r.source)?.name ?? r.source}</td>
                    <td>{r.startedBy}</td>
                    <td>{STATUS[r.status]}</td>
                    <td>{r.pagesListed.toLocaleString()}</td>
                    <td>{r.pagesFetched.toLocaleString()}</td>
                    <td>{r.pagesUnchanged.toLocaleString()}</td>
                    <td>{r.pagesFailed.toLocaleString()}</td>
                    <td>{r.productsMatched.toLocaleString()}</td>
                    <td>{r.imagesSaved.toLocaleString()}</td>
                    <td className="muted">{r.lastError ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function SettingsCard({ s, onSaved }: { s: Overview['settings']; onSaved: () => void }) {
  const [nightly, setNightly] = useState(s.nightly);
  const [hour, setHour] = useState(s.hour);
  const [autoAdd, setAutoAdd] = useState(s.autoAddProducts);
  const [webPhotos, setWebPhotos] = useState(s.photosForWebProducts);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [ok, setOk] = useState(false);
  return (
    <form
      className="card"
      onSubmit={async (e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        setError(null);
        setOk(false);
        try {
          await api('/web-sync/settings', { method: 'PUT', body: { nightly, hour, autoAddProducts: autoAdd, photosForWebProducts: webPhotos } });
          setOk(true);
          onSaved();
        } catch (err) {
          setError(err);
        } finally {
          setBusy(false);
        }
      }}
    >
      <h2>Settings</h2>
      <div className="grid3">
        <label className="check">
          <input type="checkbox" checked={nightly} onChange={(e) => setNightly(e.target.checked)} /> Update automatically every night
        </label>
        <Field label="Start time (Toronto)">
          <select value={hour} onChange={(e) => setHour(Number(e.target.value))} disabled={!nightly}>
            {Array.from({ length: 24 }, (_, h) => (
              <option key={h} value={h}>
                {String(h).padStart(2, '0')}:00
              </option>
            ))}
          </select>
        </Field>
      </div>
      <label className="check">
        <input type="checkbox" checked={autoAdd} onChange={(e) => setAutoAdd(e.target.checked)} /> After each run, add items found online that are not in your products
      </label>
      <label className="check">
        <input type="checkbox" checked={webPhotos} onChange={(e) => setWebPhotos(e.target.checked)} /> Also download Costco photos for those items (many more requests and much more
        disk space)
      </label>
      <ErrorBox error={error} />
      {ok && <Success>Saved.</Success>}
      <button className="btn btn-primary" disabled={busy}>
        {busy ? 'Saving…' : 'Save settings'}
      </button>
    </form>
  );
}
